import VoltIconSvg from '@app/assets/svg/VoltIcon';
import useSingleRemoteConfig from '@app/hooks/useSingleRemoteConfig';
import Button from '@app/sharedComponents/Button/Button';
import { useAppSelector } from '@app/store/hooks';
import { ThemedText, ThemedView } from '@app/theme';
import { isFromAndroidBrowser, isFromiOSBrowser, isLASFlowEnabled } from '@app/utils';
import { trackEvent } from '@app/utils/amplitude';
import { Linking } from 'react-native';

/** Remote config `show_download_card`: JSON string array of platform codes. */
function parseShowDownloadCardList(raw: string | null): string[] {
  if (raw == null || raw.trim() === '') {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map(String).map((s) => s.trim()).filter(Boolean);
  } catch {
    return [];
  }
}

const DownloadAppCard = () => {
  const { configValue: showDownloadCardRaw, loading: showDownloadCardLoading } =
    useSingleRemoteConfig('show_download_card');
  const userDetails = useAppSelector((state) => state.auth.userDetails);

  const platformCode =
    userDetails?.linkedPlatformAccounts?.[0]?.platformCode?.trim() ?? '';

  const allowedCodes = parseShowDownloadCardList(showDownloadCardRaw);
  const isPlatformAllowed =
    platformCode !== '' && allowedCodes.includes(platformCode);

  const isLMSUser = useAppSelector((state) => state.commonReducer.isLMSUser);
  const isLoanCompleted = useAppSelector((state) => state.commonReducer.isLoanCompleted);
  const referralBanner = useAppSelector((state) => state.commonReducer.referralBanner);
  const isLasFlowEnabled = isLASFlowEnabled();

  if (showDownloadCardLoading || !isPlatformAllowed) {
    return null;
  }

  const refereeBanner = referralBanner?.find((b: any) => b.referralParticipantType === 'REFEREE');
  const isReferredUser = !!refereeBanner;
  const referrerName = refereeBanner?.bannerDetails?.name ?? null;
  const isPostPledge = isLMSUser && (isLoanCompleted || isLasFlowEnabled === false);
  const sourcePage = isPostPledge ? 'lms_dashboard' : 'home_dashboard';
  const applicationStage = isPostPledge ? 'post_pledge' : 'post_eligibility';

  const handleDownloadPress = () => {
    trackEvent('HOME_DOWNLOADAPP_BUTTON_CLICKED', {
      source_page: sourcePage,
      is_referred_user: isReferredUser,
      referrer_name: referrerName,
      application_stage: applicationStage,
    });
    if (isFromAndroidBrowser()) {
      Linking.openURL('https://play.google.com/store/apps/details?id=in.voltmoney');
    } else if (isFromiOSBrowser()) {
      Linking.openURL('https://apps.apple.com/in/app/volt-money-mutual-fund-loan/id6446775789');
    } else {
      Linking.openURL('https://play.google.com/store/apps/details?id=in.voltmoney');
    }
  };

  return (
    <ThemedView
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor="bg-surface-primary"
      py="md"
      px="md"
      mx="lg"
      mt='lg'
      mb="lg"
      borderRadius="md"
      borderWidth={1}
      borderColor="border-primary"
    >
      <ThemedView flexDirection="row" alignItems="center" flex={1} mr="md">
        <VoltIconSvg />
        <ThemedView flex={1} ml="sm">
          <ThemedText variant="b4s" color="text-primary" mb="2xs" numberOfLines={1}>
            Download Volt money app
          </ThemedText>
          <ThemedText variant="b4r" color="text-secondary" numberOfLines={1}>
            Get a superior experience
          </ThemedText>
        </ThemedView>
      </ThemedView>

      <ThemedView>
        <Button
          label="Get app"
          size="SM"
          type="SOLID"
          handlePress={handleDownloadPress}
          overRideStyle={{ borderRadius: 20, paddingHorizontal: 12 }}
        />
      </ThemedView>
    </ThemedView>
  );
};

export default DownloadAppCard;
